import { Button } from "@features/atoms/Button"
import { useCheckout } from "@features/tickets/useCheckout"
import { formatDollars } from "@utils/format-dollars"
import { cn } from "@utils/tw"
import { defineBlock, EditableText, useInlineEditableValue } from "eddev/blocks"
import { AnimatePresence, motion, MotionProps } from "motion/react"
import { useState } from "react"

export const meta: BlockMeta = {
  title: "Membership Levels",
  description: "Displays the available membership levels",
  category: "text",
  icon: "admin-users",
  tags: ["#page"],
  flags: {
    fullWidth: true,
  },
}

export default defineBlock("memberships/membership-levels", (props) => {
  const checkout = useCheckout()
  const levels = props.miscSettings?.memberships?.membershipLevels ?? []
  const [listFocused, setListFocused] = useState<null | number>(null)
  const [mode, setMode] = useState<"plus" | "standard">("standard")
  const [purchasingId, setPurchasingId] = useState<string | null>(null)

  return (
    <div className="content-sized block-spacing">
      <div className="grid-auto gap-y-grid-margin">
        {levels.map((level, i) => {
          let key = "standard"
          let price = formatDollars(level?.priceStandard!)
          if (mode === "plus" && level?.hasPremium) {
            key = "plus"
            price = formatDollars(level?.pricePremium!)
          }
          const membershipId = key === "plus" ? level?.membershipIdPremium : level?.membershipIdStandard

          const tabUnderline = (
            <motion.div
              layoutId={"membership_tabs_" + i}
              className="absolute left-0 right-0 bottom-0 h-[1px] bg-black"
            />
          )

          return (
            <div
              key={i}
              className="col-span-12 sm:col-span-6 md:col-span-4 p-4 lg:p-5 bg-grey2 flex flex-col text-blue relative"
            >
              <div className="w-full h-full border border-blue absolute left-[6.4px] top-[6.4px] pointer-events-none bg-white" />
              <div className="w-full h-full border border-blue absolute left-0 top-0 pointer-events-none bg-white" />

              <h3 className="type-body-l flex flex-col gap-2 relative">
                <span>{level?.label} </span>
                <span className="opacity-50 type-title-xs inline-block">
                  <AnimatePresence initial={false} mode="wait">
                    <motion.div key={key} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      {price}/YR
                    </motion.div>
                  </AnimatePresence>
                </span>
              </h3>
              {!!level?.hasPremium && (
                <motion.div layoutRoot className="flex mt-2 gap-2 relative">
                  <button
                    className={cn("type-label-s py-1 relative", mode !== "standard" && "opacity-50")}
                    onClick={() => setMode("standard")}
                  >
                    Standard
                    {mode === "standard" && tabUnderline}
                  </button>
                  <button
                    className={cn("type-label-s py-1 relative", mode !== "plus" && "opacity-50")}
                    onClick={() => setMode("plus")}
                  >
                    Premium
                    {mode === "plus" && tabUnderline}
                  </button>
                </motion.div>
              )}
              <div className="w-full mt-6 mb-4 h-[1px] bg-blue relative" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={key}
                  variants={{
                    enter: {},
                    show: { transition: { staggerChildren: 0.05 } },
                    leave: { transition: { duration: 0.1 } },
                  }}
                  initial="enter"
                  animate="show"
                  exit="leave"
                  className="relative"
                >
                  {/* <motion.div className="type-body-s mt-3" {...fadeVariants}>
                    <EditableText
                      store={`level_description_${level?.label}_${key}`}
                      placeholder="Enter a description for this level"
                    />
                  </motion.div> */}
                  <div className="type-body-s mt-3 relative">
                    <EditableText
                      as="div"
                      className={cn(listFocused !== i && "absolute opacity-0 inset-0 z-10")}
                      store={`level_items_${level?.label}_${key}`}
                      placeholder="Enter dot points for this level"
                      onFocus={() => setListFocused(i)}
                      onBlur={() => setListFocused(-1)}
                    />
                    {listFocused != i && <TickList id={`level_items_${level?.label}_${key}`} />}
                  </div>
                </motion.div>
              </AnimatePresence>
              <div className="flex-1 flex flex-col justify-end pt-7">
                <Button
                  onClick={async () => {
                    if (env.admin || !membershipId) return
                    setPurchasingId(membershipId)
                    try {
                      await checkout.openMembershipPurchase(membershipId)
                    } finally {
                      setPurchasingId(null)
                    }
                  }}
                  disabled={!env.admin && (!membershipId || !checkout.basket || checkout.isPending)}
                  loading={!!membershipId && purchasingId === membershipId}
                  variant="filled"
                  className="!w-full !justify-center"
                >
                  {membershipId || env.admin ? (
                    <EditableText
                      store={`level_button_${level?.label}_${key}`}
                      placeholder="Become a Tower + MFF Member"
                      defaultValue="Become a Tower + MFF Member"
                    />
                  ) : (
                    "Currently unavailable"
                  )}
                </Button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
})

const fadeVariants: MotionProps = {
  variants: {
    enter: { opacity: 0, y: 0 },
    show: { opacity: 1, y: 0, transition: { duration: 0.1, ease: "easeOut" } },
    leave: { opacity: 0, y: 0, transition: { duration: 0.1 } },
  },
  initial: "enter",
  animate: "show",
  exit: "leave",
}

function TickList(props: { id: string }) {
  const [text] = useInlineEditableValue(props.id, "")
  let items = text
    .split(/<br\s?\/?>/g)
    .map((i) => i.trim())
    .filter((i) => !!i)

  if (items.length === 0) {
    if (env.admin) {
      items = ["Click to add dot points"]
    }
  }

  return (
    <motion.ul className="flex flex-col gap-2">
      {items.map((item, i) => (
        <motion.li key={i} className="flex gap-1" {...fadeVariants}>
          <span className="w-4 shrink-0 flex" dangerouslySetInnerHTML={{ __html: "&bull;" }} />
          <span className="flex w-full">{item}</span>
        </motion.li>
      ))}
    </motion.ul>
  )
}
